import { deleteFeatureFlag } from '@gonfalon/openapi/operations/deleteFeatureFlag';
import { getAudienceSeen } from '@gonfalon/openapi/operations/getAudienceSeen';
import { getDependentFlags } from '@gonfalon/openapi/operations/getDependentFlags';
import { getDependentFlagsByEnv } from '@gonfalon/openapi/operations/getDependentFlagsByEnv';
import { getEvaluationsUsage } from '@gonfalon/openapi/operations/getEvaluationsUsage';
import { getEvaluationsWithContextKind } from '@gonfalon/openapi/operations/getEvaluationsWithContextKind';
import { getFeatureFlag } from '@gonfalon/openapi/operations/getFeatureFlag';
import { getFeatureFlags1 } from '@gonfalon/openapi/operations/getFeatureFlags1';
import { getFeatureFlagStatus } from '@gonfalon/openapi/operations/getFeatureFlagStatus';
import { getFeatureFlagStatusAcrossEnvironments } from '@gonfalon/openapi/operations/getFeatureFlagStatusAcrossEnvironments';
import { getFeatureFlagStatuses } from '@gonfalon/openapi/operations/getFeatureFlagStatuses';
import { getFeatureFlagTemplatesForProject } from '@gonfalon/openapi/operations/getFeatureFlagTemplatesForProject';
import { getFlagArchiveChecks } from '@gonfalon/openapi/operations/getFlagArchiveChecks';
import { getFlagFollowers } from '@gonfalon/openapi/operations/getFlagFollowers';
import { getFlagLinks } from '@gonfalon/openapi/operations/getFlagLinks';
import { getReleaseByFlagKey } from '@gonfalon/openapi/operations/getReleaseByFlagKey';
import { internalCompareFlagsAcrossEnvironments } from '@gonfalon/openapi/operations/internalCompareFlagsAcrossEnvironments';
import { internalGetFlagConfigDependencies } from '@gonfalon/openapi/operations/internalGetFlagConfigDependencies';
import { internalGetFlagsTargetingSegment } from '@gonfalon/openapi/operations/internalGetFlagsTargetingSegment';
import { internalGetResolvedPrerequisites } from '@gonfalon/openapi/operations/internalGetResolvedPrerequisites';
import { internalGetTargetingAnalysis } from '@gonfalon/openapi/operations/internalGetTargetingAnalysis';
import { patchFeatureFlagTemplate } from '@gonfalon/openapi/operations/patchFeatureFlagTemplate';
import type { operations } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createBatchedQueryOptions } from '../internal/createBatchedQueryOptions';
import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { internalSearchFlagsTaskRunner } from '../internal/internalSearchFlagsTaskRunner';
import {
  queryFeatureFlagStatusesTaskRunner,
  queryFeatureFlagStatusesWithPartialReturnTaskRunner,
} from '../internal/queryFeatureFlagStatusesTaskRunner';

export const getFeatureFlagsQuery = createQueryOptions(getFeatureFlags1);
export const deleteFeatureFlagMutation = createMutationHook(deleteFeatureFlag, {
  onSuccess: async (_, { projectKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getFeatureFlagsQuery({ projectKey }).queryKey });
  },
});
export const getInfiniteFeatureFlagsQuery = createInfiniteQueryOptions(getFeatureFlags1);
export const getFeatureFlagQuery = createQueryOptions(getFeatureFlag);
export const getFeatureFlagStatusQuery = createQueryOptions(getFeatureFlagStatus);
export const getFlagStatusesQueryOptions = createQueryOptions(getFeatureFlagStatuses);
export const searchFlagsQuery = createBatchedQueryOptions(internalSearchFlagsTaskRunner);
export const getFlagConfigDependenciesQuery = createQueryOptions(internalGetFlagConfigDependencies);

export const getFlagLinksQuery = createQueryOptions(getFlagLinks);

export const getFlagFollowersQuery = createQueryOptions(getFlagFollowers);

export const getAudienceSeenQuery = createQueryOptions(getAudienceSeen);

export const getDependentFlagsQuery = createQueryOptions(getDependentFlags);
export const getDependentFlagsByEnvQuery = createQueryOptions(getDependentFlagsByEnv);

export const getEvaluationsUsageQuery = createQueryOptions(getEvaluationsUsage);

export const getReleaseByFlagKeyQuery = createQueryOptions(getReleaseByFlagKey);

export const getFeatureFlagStatusAcrossEnvironmentsQuery = createQueryOptions(getFeatureFlagStatusAcrossEnvironments);

export const getFeatureFlagStatusesQuery = createBatchedQueryOptions(queryFeatureFlagStatusesTaskRunner);

/**
 * Query for feature flag statuses with partial return support.
 * This uses the newer API endpoint that allows partial results when some flags are missing,
 * making it more resilient to timing issues during flag creation.
 *
 * Use this for flag environment cards and other UI components that should gracefully handle missing flags.
 */
export const getFeatureFlagStatusesWithPartialReturnQuery = createBatchedQueryOptions(
  queryFeatureFlagStatusesWithPartialReturnTaskRunner,
);

export const getFeatureFlagTemplatesForProjectQuery = createQueryOptions(getFeatureFlagTemplatesForProject);

export const useUpdateFeatureFlagTemplate = createMutationHook(patchFeatureFlagTemplate, {
  onSuccess: async (_, { projectKey }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: getFeatureFlagTemplatesForProjectQuery.partialQueryKey({ projKey: projectKey }),
    });
  },
});

export const getFlagContextKindDiagnosticsQuery = createQueryOptions(getEvaluationsWithContextKind);
export const getFlagTargetingAnalysisQuery = createQueryOptions(internalGetTargetingAnalysis);

export const getFlagArchiveChecksQuery = createQueryOptions(getFlagArchiveChecks);

export const getCompareFlagsQuery = createQueryOptions(internalCompareFlagsAcrossEnvironments);
export const getSegmentTargetedFlagsQuery = createQueryOptions(internalGetFlagsTargetingSegment);
export const internalGetResolvedPrerequisitesQuery = createQueryOptions(internalGetResolvedPrerequisites);
export type StaleFlagDetails = NonNullable<
  operations['getFlagArchiveChecks']['responses']['200']['content']['application/json']['details']
>;
export type StaleFlagConfigDetails = NonNullable<
  NonNullable<
    operations['getFlagArchiveChecks']['responses']['200']['content']['application/json']['details']
  >['perEnvironmentDetails']
>[string];
